/**
 * A color.
 */
export class Color {
  /**
   * The color's ID.
   *
   * This is also the ANSI escape code for the color used in the prompt string.
   */
  id: number;

  /**
   * The color's display name.
   */
  name: string;

  /**
   * The color's hex code, including a leading `#`.
   */
  hex: string;

  /**
   * If the color is a 4-bit color, holds the according color name and the (shorter) escape codes.
   *
   * Otherwise, `null` (for 8-bit colors).
   */
  color16: {
    name: string;

    foreground: number;

    background: number;
  } | null;

  constructor(
    id: number,
    name: string,
    hex: string,
    color16: { name: string; foreground: number; background: number } | null,
  ) {
    this.id = id;
    this.name = name;
    this.hex = hex;
    this.color16 = color16;
  }
}

/**
 * The list of all 256 8-bit colors.
 */
export const COLORS = [
  new Color(0, 'Black', '#000000', { name: 'Black', foreground: 30, background: 40 }),
  new Color(1, 'Maroon', '#800000', { name: 'Red', foreground: 31, background: 41 }),
  new Color(2, 'Green', '#008000', { name: 'Green', foreground: 32, background: 42 }),
  new Color(3, 'Olive', '#808000', { name: 'Yellow', foreground: 33, background: 43 }),
  new Color(4, 'Navy', '#000080', { name: 'Blue', foreground: 34, background: 44 }),
  new Color(5, 'Purple', '#800080', { name: 'Magenta', foreground: 35, background: 45 }),
  new Color(6, 'Teal', '#008080', { name: 'Cyan', foreground: 36, background: 46 }),
  new Color(7, 'Silver', '#c0c0c0', { name: 'White', foreground: 37, background: 47 }),
  new Color(8, 'Grey', '#808080', { name: 'BrightBlack', foreground: 90, background: 100 }),
  new Color(9, 'Red', '#ff0000', { name: 'BrightRed', foreground: 91, background: 101 }),
  new Color(10, 'Lime', '#00ff00', { name: 'BrightGreen', foreground: 92, background: 102 }),
  new Color(11, 'Yellow', '#ffff00', { name: 'BrightYellow', foreground: 93, background: 103 }),
  new Color(12, 'Blue', '#0000ff', { name: 'BrightBlue', foreground: 94, background: 104 }),
  new Color(13, 'Fuchsia', '#ff00ff', { name: 'BrightMagenta', foreground: 95, background: 105 }),
  new Color(14, 'Aqua', '#00ffff', { name: 'BrightCyan', foreground: 96, background: 106 }),
  new Color(15, 'White', '#ffffff', { name: 'BrightWhite', foreground: 97, background: 107 }),
  new Color(16, 'Grey0', '#000000', null),
  new Color(17, 'NavyBlue', '#00005f', null),
  new Color(18, 'DarkBlue', '#000087', null),
  new Color(19, 'Blue3', '#0000af', null),
  new Color(20, 'Blue3', '#0000d7', null),
  new Color(21, 'Blue1', '#0000ff', null),
  new Color(22, 'DarkGreen', '#005f00', null),
  new Color(23, 'DeepSkyBlue4', '#005f5f', null),
  new Color(24, 'DeepSkyBlue4', '#005f87', null),
  new Color(25, 'DeepSkyBlue4', '#005faf', null),
  new Color(26, 'DodgerBlue3', '#005fd7', null),
  new Color(27, 'DodgerBlue2', '#005fff', null),
  new Color(28, 'Green4', '#008700', null),
  new Color(29, 'SpringGreen4', '#00875f', null),
  new Color(30, 'Turquoise4', '#008787', null),
  new Color(31, 'DeepSkyBlue3', '#0087af', null),
  new Color(32, 'DeepSkyBlue3', '#0087d7', null),
  new Color(33, 'DodgerBlue1', '#0087ff', null),
  new Color(34, 'Green3', '#00af00', null),
  new Color(35, 'SpringGreen3', '#00af5f', null),
  new Color(36, 'DarkCyan', '#00af87', null),
  new Color(37, 'LightSeaGreen', '#00afaf', null),
  new Color(38, 'DeepSkyBlue2', '#00afd7', null),
  new Color(39, 'DeepSkyBlue1', '#00afff', null),
  new Color(40, 'Green3', '#00d700', null),
  new Color(41, 'SpringGreen3', '#00d75f', null),
  new Color(42, 'SpringGreen2', '#00d787', null),
  new Color(43, 'Cyan3', '#00d7af', null),
  new Color(44, 'DarkTurquoise', '#00d7d7', null),
  new Color(45, 'Turquoise2', '#00d7ff', null),
  new Color(46, 'Green1', '#00ff00', null),
  new Color(47, 'SpringGreen2', '#00ff5f', null),
  new Color(48, 'SpringGreen1', '#00ff87', null),
  new Color(49, 'MediumSpringGreen', '#00ffaf', null),
  new Color(50, 'Cyan2', '#00ffd7', null),
  new Color(51, 'Cyan1', '#00ffff', null),
  new Color(52, 'DarkRed', '#5f0000', null),
  new Color(53, 'DeepPink4', '#5f005f', null),
  new Color(54, 'Purple4', '#5f0087', null),
  new Color(55, 'Purple4', '#5f00af', null),
  new Color(56, 'Purple3', '#5f00d7', null),
  new Color(57, 'BlueViolet', '#5f00ff', null),
  new Color(58, 'Orange4', '#5f5f00', null),
  new Color(59, 'Grey37', '#5f5f5f', null),
  new Color(60, 'MediumPurple4', '#5f5f87', null),
  new Color(61, 'SlateBlue3', '#5f5faf', null),
  new Color(62, 'SlateBlue3', '#5f5fd7', null),
  new Color(63, 'RoyalBlue1', '#5f5fff', null),
  new Color(64, 'Chartreuse4', '#5f8700', null),
  new Color(65, 'DarkSeaGreen4', '#5f875f', null),
  new Color(66, 'PaleTurquoise4', '#5f8787', null),
  new Color(67, 'SteelBlue', '#5f87af', null),
  new Color(68, 'SteelBlue3', '#5f87d7', null),
  new Color(69, 'CornflowerBlue', '#5f87ff', null),
  new Color(70, 'Chartreuse3', '#5faf00', null),
  new Color(71, 'DarkSeaGreen4', '#5faf5f', null),
  new Color(72, 'CadetBlue', '#5faf87', null),
  new Color(73, 'CadetBlue', '#5fafaf', null),
  new Color(74, 'SkyBlue3', '#5fafd7', null),
  new Color(75, 'SteelBlue1', '#5fafff', null),
  new Color(76, 'Chartreuse3', '#5fd700', null),
  new Color(77, 'PaleGreen3', '#5fd75f', null),
  new Color(78, 'SeaGreen3', '#5fd787', null),
  new Color(79, 'Aquamarine3', '#5fd7af', null),
  new Color(80, 'MediumTurquoise', '#5fd7d7', null),
  new Color(81, 'SteelBlue1', '#5fd7ff', null),
  new Color(82, 'Chartreuse2', '#5fff00', null),
  new Color(83, 'SeaGreen2', '#5fff5f', null),
  new Color(84, 'SeaGreen1', '#5fff87', null),
  new Color(85, 'SeaGreen1', '#5fffaf', null),
  new Color(86, 'Aquamarine1', '#5fffd7', null),
  new Color(87, 'DarkSlateGray2', '#5fffff', null),
  new Color(88, 'DarkRed', '#870000', null),
  new Color(89, 'DeepPink4', '#87005f', null),
  new Color(90, 'DarkMagenta', '#870087', null),
  new Color(91, 'DarkMagenta', '#8700af', null),
  new Color(92, 'DarkViolet', '#8700d7', null),
  new Color(93, 'Purple', '#8700ff', null),
  new Color(94, 'Orange4', '#875f00', null),
  new Color(95, 'LightPink4', '#875f5f', null),
  new Color(96, 'Plum4', '#875f87', null),
  new Color(97, 'MediumPurple3', '#875faf', null),
  new Color(98, 'MediumPurple3', '#875fd7', null),
  new Color(99, 'SlateBlue1', '#875fff', null),
  new Color(100, 'Yellow4', '#878700', null),
  new Color(101, 'Wheat4', '#87875f', null),
  new Color(102, 'Grey53', '#878787', null),
  new Color(103, 'LightSlateGrey', '#8787af', null),
  new Color(104, 'MediumPurple', '#8787d7', null),
  new Color(105, 'LightSlateBlue', '#8787ff', null),
  new Color(106, 'Yellow4', '#87af00', null),
  new Color(107, 'DarkOliveGreen3', '#87af5f', null),
  new Color(108, 'DarkSeaGreen', '#87af87', null),
  new Color(109, 'LightSkyBlue3', '#87afaf', null),
  new Color(110, 'LightSkyBlue3', '#87afd7', null),
  new Color(111, 'SkyBlue2', '#87afff', null),
  new Color(112, 'Chartreuse2', '#87d700', null),
  new Color(113, 'DarkOliveGreen3', '#87d75f', null),
  new Color(114, 'PaleGreen3', '#87d787', null),
  new Color(115, 'DarkSeaGreen3', '#87d7af', null),
  new Color(116, 'DarkSlateGray3', '#87d7d7', null),
  new Color(117, 'SkyBlue1', '#87d7ff', null),
  new Color(118, 'Chartreuse1', '#87ff00', null),
  new Color(119, 'LightGreen', '#87ff5f', null),
  new Color(120, 'LightGreen', '#87ff87', null),
  new Color(121, 'PaleGreen1', '#87ffaf', null),
  new Color(122, 'Aquamarine1', '#87ffd7', null),
  new Color(123, 'DarkSlateGray1', '#87ffff', null),
  new Color(124, 'Red3', '#af0000', null),
  new Color(125, 'DeepPink4', '#af005f', null),
  new Color(126, 'MediumVioletRed', '#af0087', null),
  new Color(127, 'Magenta3', '#af00af', null),
  new Color(128, 'DarkViolet', '#af00d7', null),
  new Color(129, 'Purple', '#af00ff', null),
  new Color(130, 'DarkOrange3', '#af5f00', null),
  new Color(131, 'IndianRed', '#af5f5f', null),
  new Color(132, 'HotPink3', '#af5f87', null),
  new Color(133, 'MediumOrchid3', '#af5faf', null),
  new Color(134, 'MediumOrchid', '#af5fd7', null),
  new Color(135, 'MediumPurple2', '#af5fff', null),
  new Color(136, 'DarkGoldenrod', '#af8700', null),
  new Color(137, 'LightSalmon3', '#af875f', null),
  new Color(138, 'RosyBrown', '#af8787', null),
  new Color(139, 'Grey63', '#af87af', null),
  new Color(140, 'MediumPurple2', '#af87d7', null),
  new Color(141, 'MediumPurple1', '#af87ff', null),
  new Color(142, 'Gold3', '#afaf00', null),
  new Color(143, 'DarkKhaki', '#afaf5f', null),
  new Color(144, 'NavajoWhite3', '#afaf87', null),
  new Color(145, 'Grey69', '#afafaf', null),
  new Color(146, 'LightSteelBlue3', '#afafd7', null),
  new Color(147, 'LightSteelBlue', '#afafff', null),
  new Color(148, 'Yellow3', '#afd700', null),
  new Color(149, 'DarkOliveGreen3', '#afd75f', null),
  new Color(150, 'DarkSeaGreen3', '#afd787', null),
  new Color(151, 'DarkSeaGreen2', '#afd7af', null),
  new Color(152, 'LightCyan3', '#afd7d7', null),
  new Color(153, 'LightSkyBlue1', '#afd7ff', null),
  new Color(154, 'GreenYellow', '#afff00', null),
  new Color(155, 'DarkOliveGreen2', '#afff5f', null),
  new Color(156, 'PaleGreen1', '#afff87', null),
  new Color(157, 'DarkSeaGreen2', '#afffaf', null),
  new Color(158, 'DarkSeaGreen1', '#afffd7', null),
  new Color(159, 'PaleTurquoise1', '#afffff', null),
  new Color(160, 'Red3', '#d70000', null),
  new Color(161, 'DeepPink3', '#d7005f', null),
  new Color(162, 'DeepPink3', '#d70087', null),
  new Color(163, 'Magenta3', '#d700af', null),
  new Color(164, 'Magenta3', '#d700d7', null),
  new Color(165, 'Magenta2', '#d700ff', null),
  new Color(166, 'DarkOrange3', '#d75f00', null),
  new Color(167, 'IndianRed', '#d75f5f', null),
  new Color(168, 'HotPink3', '#d75f87', null),
  new Color(169, 'HotPink2', '#d75faf', null),
  new Color(170, 'Orchid', '#d75fd7', null),
  new Color(171, 'MediumOrchid1', '#d75fff', null),
  new Color(172, 'Orange3', '#d78700', null),
  new Color(173, 'LightSalmon3', '#d7875f', null),
  new Color(174, 'LightPink3', '#d78787', null),
  new Color(175, 'Pink3', '#d787af', null),
  new Color(176, 'Plum3', '#d787d7', null),
  new Color(177, 'Violet', '#d787ff', null),
  new Color(178, 'Gold3', '#d7af00', null),
  new Color(179, 'LightGoldenrod3', '#d7af5f', null),
  new Color(180, 'Tan', '#d7af87', null),
  new Color(181, 'MistyRose3', '#d7afaf', null),
  new Color(182, 'Thistle3', '#d7afd7', null),
  new Color(183, 'Plum2', '#d7afff', null),
  new Color(184, 'Yellow3', '#d7d700', null),
  new Color(185, 'Khaki3', '#d7d75f', null),
  new Color(186, 'LightGoldenrod2', '#d7d787', null),
  new Color(187, 'LightYellow3', '#d7d7af', null),
  new Color(188, 'Grey84', '#d7d7d7', null),
  new Color(189, 'LightSteelBlue1', '#d7d7ff', null),
  new Color(190, 'Yellow2', '#d7ff00', null),
  new Color(191, 'DarkOliveGreen1', '#d7ff5f', null),
  new Color(192, 'DarkOliveGreen1', '#d7ff87', null),
  new Color(193, 'DarkSeaGreen1', '#d7ffaf', null),
  new Color(194, 'Honeydew2', '#d7ffd7', null),
  new Color(195, 'LightCyan1', '#d7ffff', null),
  new Color(196, 'Red1', '#ff0000', null),
  new Color(197, 'DeepPink2', '#ff005f', null),
  new Color(198, 'DeepPink1', '#ff0087', null),
  new Color(199, 'DeepPink1', '#ff00af', null),
  new Color(200, 'Magenta2', '#ff00d7', null),
  new Color(201, 'Magenta1', '#ff00ff', null),
  new Color(202, 'OrangeRed1', '#ff5f00', null),
  new Color(203, 'IndianRed1', '#ff5f5f', null),
  new Color(204, 'IndianRed1', '#ff5f87', null),
  new Color(205, 'HotPink', '#ff5faf', null),
  new Color(206, 'HotPink', '#ff5fd7', null),
  new Color(207, 'MediumOrchid1', '#ff5fff', null),
  new Color(208, 'DarkOrange', '#ff8700', null),
  new Color(209, 'Salmon1', '#ff875f', null),
  new Color(210, 'LightCoral', '#ff8787', null),
  new Color(211, 'PaleVioletRed1', '#ff87af', null),
  new Color(212, 'Orchid2', '#ff87d7', null),
  new Color(213, 'Orchid1', '#ff87ff', null),
  new Color(214, 'Orange1', '#ffaf00', null),
  new Color(215, 'SandyBrown', '#ffaf5f', null),
  new Color(216, 'LightSalmon1', '#ffaf87', null),
  new Color(217, 'LightPink1', '#ffafaf', null),
  new Color(218, 'Pink1', '#ffafd7', null),
  new Color(219, 'Plum1', '#ffafff', null),
  new Color(220, 'Gold1', '#ffd700', null),
  new Color(221, 'LightGoldenrod2', '#ffd75f', null),
  new Color(222, 'LightGoldenrod2', '#ffd787', null),
  new Color(223, 'NavajoWhite1', '#ffd7af', null),
  new Color(224, 'MistyRose1', '#ffd7d7', null),
  new Color(225, 'Thistle1', '#ffd7ff', null),
  new Color(226, 'Yellow1', '#ffff00', null),
  new Color(227, 'LightGoldenrod1', '#ffff5f', null),
  new Color(228, 'Khaki1', '#ffff87', null),
  new Color(229, 'Wheat1', '#ffffaf', null),
  new Color(230, 'Cornsilk1', '#ffffd7', null),
  new Color(231, 'Grey100', '#ffffff', null),
  new Color(232, 'Grey3', '#080808', null),
  new Color(233, 'Grey7', '#121212', null),
  new Color(234, 'Grey11', '#1c1c1c', null),
  new Color(235, 'Grey15', '#262626', null),
  new Color(236, 'Grey19', '#303030', null),
  new Color(237, 'Grey23', '#3a3a3a', null),
  new Color(238, 'Grey27', '#444444', null),
  new Color(239, 'Grey30', '#4e4e4e', null),
  new Color(240, 'Grey35', '#585858', null),
  new Color(241, 'Grey39', '#626262', null),
  new Color(242, 'Grey42', '#6c6c6c', null),
  new Color(243, 'Grey46', '#767676', null),
  new Color(244, 'Grey50', '#808080', null),
  new Color(245, 'Grey54', '#8a8a8a', null),
  new Color(246, 'Grey58', '#949494', null),
  new Color(247, 'Grey62', '#9e9e9e', null),
  new Color(248, 'Grey66', '#a8a8a8', null),
  new Color(249, 'Grey70', '#b2b2b2', null),
  new Color(250, 'Grey74', '#bcbcbc', null),
  new Color(251, 'Grey78', '#c6c6c6', null),
  new Color(252, 'Grey82', '#d0d0d0', null),
  new Color(253, 'Grey85', '#dadada', null),
  new Color(254, 'Grey89', '#e4e4e4', null),
  new Color(255, 'Grey93', '#eeeeee', null),
];
